import * as THREE from 'three';
import Application from '../Application';
/**
 * Reprend les sources lumineuses de la scene de reference du dossier 1
 * (my-room-in-3d) : le panneau Elgato, l'anneau de LED de l'enceinte et les
 * touches retroeclairees du Loupedeck.
 *
 * Ce sont de simples plans emissifs. Toute la scene du dossier 3 est rendue
 * en MeshBasicMaterial (non eclairee), donc une couleur vive y lit
 * naturellement comme une source lumineuse : aucune lumiere Three.js n'est
 * ajoutee, et le cout d'affichage reste nul.
 *
 * Les couleurs d'origine sont remplacees par celles du logo. Elles en
 * etaient deja proches (#196aff contre #2f6bff, #ff5d00 contre #ff7a1a),
 * mais les reprendre du kit garde la scene alignee sur la charte.
 *
 * Positions exprimees en fractions des dimensions du bureau et resolues a
 * l'execution, comme pour le reste du decor importe.
 */
const BRAND = [0x2f6bff, 0x12c2b0, 0xff7a1a, 0xffb547];
const LIGHTS = [
    // Anneau de LED de l'enceinte, pose sur le plateau.
    { resource: 'googleLedsModel', name: 'leds', offset: [0.44, 0.12], lift: 0.02, width: 0.09 },
    // Touches retroeclairees, a gauche du clavier.
    { resource: 'loupedeckModel', name: 'loupedeck', offset: [-0.4, 0.2], lift: 0.01, width: 0.15, yaw: 8 },
];
export default class RoomLights {
    constructor() {
        this.group = null;
        this.pulsing = [];
        this.reducedMotion = false;
        this.application = new Application();
        this.scene = this.application.scene;
        this.resources = this.application.resources;
        this.time = this.application.time;
        this.reducedMotion =
            typeof window.matchMedia === 'function' &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.place();
    }
    /** Reglages sans rebuild : ?lights=0 masque tout, ?lightsScale= ajuste. */
    params() {
        const p = new URLSearchParams(window.location.search);
        const n = parseFloat(p.get('lightsScale') || '1');
        return {
            enabled: p.get('lights') !== '0',
            scale: Number.isFinite(n) ? n : 1,
        };
    }
    place() {
        var _a;
        const { enabled, scale } = this.params();
        if (!enabled)
            return;
        const desk = this.scene.getObjectByName('desk');
        if (!desk)
            return;
        const deskBox = new THREE.Box3().setFromObject(desk);
        const deskSize = deskBox.getSize(new THREE.Vector3());
        const deskCenter = deskBox.getCenter(new THREE.Vector3());
        const group = new THREE.Group();
        group.name = 'room_lights';
        let colorIndex = 0;
        for (const spec of LIGHTS) {
            const model = this.resources.items.gltfModel[spec.resource];
            if (!model)
                continue;
            const obj = model.scene.clone(true);
            obj.position.set(0, 0, 0);
            obj.rotation.set(0, 0, 0);
            obj.scale.setScalar(1);
            // Chaque plan recoit son propre materiau : les LED et les touches
            // doivent pouvoir s'allumer independamment.
            obj.traverse((child) => {
                var _a;
                if (!(child instanceof THREE.Mesh))
                    return;
                const color = (_a = spec.color) !== null && _a !== void 0 ? _a : BRAND[colorIndex++ % BRAND.length];
                const material = new THREE.MeshBasicMaterial({
                    color,
                    toneMapped: false,
                });
                // La couleur de base est conservee : l'animation repart
                // d'elle a chaque image, sinon la multiplication se cumule
                // et les LED s'eteindraient progressivement.
                material.userData.base = color;
                child.material = material;
                if (spec.color === undefined)
                    this.pulsing.push(material);
            });
            obj.updateMatrixWorld(true);
            const raw = new THREE.Box3().setFromObject(obj);
            const rawSize = raw.getSize(new THREE.Vector3());
            if (rawSize.x <= 0)
                continue;
            obj.scale.setScalar((deskSize.x * spec.width * scale) / rawSize.x);
            obj.rotation.y = THREE.MathUtils.degToRad((_a = spec.yaw) !== null && _a !== void 0 ? _a : 0);
            obj.updateMatrixWorld(true);
            const placed = new THREE.Box3().setFromObject(obj);
            const placedCenter = placed.getCenter(new THREE.Vector3());
            obj.position.x += deskCenter.x + deskSize.x * spec.offset[0] - placedCenter.x;
            obj.position.z += deskCenter.z + deskSize.z * spec.offset[1] - placedCenter.z;
            obj.position.y += deskBox.max.y + deskSize.y * spec.lift - placed.min.y;
            group.add(obj);
        }
        this.group = group;
        this.scene.add(group);
    }
    update() {
        // Respiration lente des LED. Coupee si le visiteur a demande moins
        // d'animations : la scene reste allumee, simplement fixe.
        if (this.reducedMotion || !this.pulsing.length)
            return;
        const t = this.time.elapsed / 1000;
        this.pulsing.forEach((material, i) => {
            const wave = 0.75 + 0.25 * Math.sin(t * 1.6 + i * 0.9);
            material.color.setHex(material.userData.base).multiplyScalar(wave);
        });
    }
}
